import { useEffect, useSyncExternalStore } from 'react';
import { Check } from '../../icons';
import { useCalendarThemeStore, sanitizeTokens } from '../../lib/calendar-theme';
import { tSettings, getSettingsLang, subscribeSettingsLang } from '../../lib/settings-i18n';

/**
 * 캘린더 테마 탭 (2026-06-07 T5) — 내 프로필 §캘린더 테마.
 *
 * admin 등록 테마(enabled) 카탈로그를 카드로 보여주고, 선택 시 즉시 적용 + UserSettings 영속.
 * 각 카드는 테마 tokens 로 그린 미니 캘린더 프리뷰. 선택한 키가 카탈로그에 없으면(비활성/삭제)
 * 안내 후 기본 적용 — calendar-theme store 의 vars() 가 {} 로 fallback.
 */

const CATEGORY_VARS = [
  '--cal-type-meeting', '--cal-type-personal', '--cal-type-external',
  '--cal-type-todo', '--cal-type-vacation',
] as const;
const ACCENT_FALLBACK: Record<string, string> = {
  '--cal-type-meeting': 'var(--accent-blue)', '--cal-type-personal': 'var(--accent-green)',
  '--cal-type-external': 'var(--accent-pink)', '--cal-type-todo': 'var(--accent-orange)',
  '--cal-type-vacation': 'var(--accent-teal)',
};

function MiniPreview({ tokens }: { tokens: Record<string, string> }) {
  const safe = sanitizeTokens(tokens) as Record<string, string>;
  const cellBg = safe['--cal-cell-bg'] ?? 'var(--surface)';
  const border = safe['--cal-border'] ?? 'var(--border)';
  const headerBg = safe['--cal-header-bg'] ?? 'var(--surface-2)';
  return (
    <div className="caltheme-preview" style={{ borderColor: border }} aria-hidden>
      <div className="caltheme-preview-head" style={{ background: headerBg }} />
      <div className="caltheme-preview-grid">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="caltheme-preview-cell" style={{ background: cellBg, borderColor: border }} />
        ))}
      </div>
      <div className="caltheme-preview-bars">
        {CATEGORY_VARS.map((v) => (
          <span key={v} className="caltheme-preview-bar" style={{ background: safe[v] ?? ACCENT_FALLBACK[v] }} />
        ))}
      </div>
    </div>
  );
}

export function CalendarThemeTab() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const catalog = useCalendarThemeStore((s) => s.catalog);
  const activeKey = useCalendarThemeStore((s) => s.activeKey);
  const setActiveKey = useCalendarThemeStore((s) => s.setActiveKey);
  const ensureLoaded = useCalendarThemeStore((s) => s.ensureLoaded);
  const reloadCatalog = useCalendarThemeStore((s) => s.reloadCatalog);

  useEffect(() => {
    void ensureLoaded().then(() => reloadCatalog()); // 탭 진입 시 최신 카탈로그 반영
  }, [ensureLoaded, reloadCatalog]);

  const selectedMissing = activeKey !== 'default' && !catalog.some((c) => c.theme_key === activeKey);

  return (
    <section className="settings-section" aria-labelledby="caltheme-h2">
      <h2 id="caltheme-h2">{tSettings('settings.caltheme.heading')}</h2>
      <p className="settings-section-subtitle">{tSettings('settings.caltheme.subtitle')}</p>

      {selectedMissing && (
        <p className="settings-error" role="alert">{tSettings('settings.caltheme.missing')}</p>
      )}

      <div className="caltheme-grid" role="radiogroup" aria-label={tSettings('settings.caltheme.heading')}>
        {catalog.length === 0 && (
          <div className="caltheme-empty">{tSettings('settings.caltheme.empty')}</div>
        )}
        {catalog.map((t) => {
          const selected = t.theme_key === activeKey || (activeKey === 'default' && t.theme_key === 'default');
          return (
            <button
              key={t.theme_key}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`caltheme-card${selected ? ' selected' : ''}`}
              onClick={() => setActiveKey(t.theme_key)}
            >
              <MiniPreview tokens={t.tokens} />
              <div className="caltheme-card-meta">
                <span className="caltheme-card-name">{t.name}</span>
                {selected && <Check size={14} aria-hidden className="caltheme-card-check" />}
              </div>
              {t.description && <span className="caltheme-card-desc">{t.description}</span>}
            </button>
          );
        })}
      </div>
    </section>
  );
}
